import { useEffect, useRef, useState } from "react";
import { format, subDays } from "date-fns";
import { useAppSelector } from "@/redux/features/hooks";
import { useMercureSubscription } from "@/lib/hooks";
import PageTitle from "@/components/PageTitle";
import DashLayout from "@/layouts/DashLayout";
import RoleProtectedPage from "@/layouts/RoleProtectedPage";
import { Roles } from "@/redux/services/user/user.type";
import { DateRangePicker } from "@/components/Dashboard/DateRangePicker";
import { KPICard } from "@/components/Dashboard/KPICard";
import {
  useGetKPIsQuery,
  useGetTimeAnalyticsQuery,
  useGetMenuPerformancesQuery,
  useGetCustomerEngagementQuery,
} from "@/redux/services/dashboard/dashboard.api";
import {
  ShoppingCart,
  TrendingUp,
  Receipt,
  CheckCircle2,
  Package,
  Users,
  Eye,
  MousePointer,
} from "lucide-react";
import {
  DailyTrendsChart,
  HourlyDistributionChart,
  DayOfWeekChart,
} from "@/components/Dashboard/TimeAnalytics";
import {
  TopSellingItems,
  SectionBreakdownChart,
} from "@/components/Dashboard/MenuPerformance";
import { EngagementTrendsChart } from "@/components/Dashboard/CustomerEngagement";
import { ErrorMessage } from "@/components/Dashboard/ErrorMessage";

function Dashboard() {
  const { currentRestaurant } = useAppSelector((s) => s.user);

  // Default to last 7 days
  const [startDate, setStartDate] = useState(
    format(subDays(new Date(), 6), "yyyy-MM-dd")
  );
  const [endDate, setEndDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [compareMode, setCompareMode] = useState(false);

  const queryParams = {
    start_date: startDate,
    end_date: endDate,
    compare: compareMode,
    restaurant_id: currentRestaurant?.id ?? "",
  };

  // Fetch all dashboard data
  const {
    data: kpiData,
    isLoading: kpiLoading,
    error: kpiError,
    refetch: refetchKpi,
  } = useGetKPIsQuery(queryParams);
  const {
    data: timeData,
    isLoading: timeLoading,
    error: timeError,
    refetch: refetchTime,
  } = useGetTimeAnalyticsQuery(queryParams);
  const {
    data: menuData,
    isLoading: menuLoading,
    error: menuError,
    refetch: refetchMenu,
  } = useGetMenuPerformancesQuery(queryParams);
  const {
    data: engagementData,
    isLoading: engagementLoading,
    error: engagementError,
    refetch: refetchEngagement,
  } = useGetCustomerEngagementQuery(queryParams);

  const handleDateChange = (newStartDate: string, newEndDate: string) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  // Keep the dashboard live: when orders are created/updated, refetch the
  // order-driven metrics so the page reflects changes without a manual reload.
  const orderEvent = useMercureSubscription(
    currentRestaurant?.id
      ? `/api/restaurants/${currentRestaurant.id}/orders`
      : null
  );
  const refetchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!orderEvent) return;
    // Coalesce bursts of order events into a single refresh.
    if (refetchTimer.current) clearTimeout(refetchTimer.current);
    refetchTimer.current = setTimeout(() => {
      refetchKpi();
      refetchTime();
      refetchMenu();
      refetchEngagement();
    }, 800);
    return () => {
      if (refetchTimer.current) clearTimeout(refetchTimer.current);
    };
  }, [orderEvent, refetchKpi, refetchTime, refetchMenu, refetchEngagement]);

  // Hide the combos card entirely for restaurants that don't use combos
  // (e.g. formula-based menus) so we never render an empty card. Keep it
  // visible while loading since we don't yet know whether combos exist.
  const showCombos =
    menuLoading ||
    (menuData?.data.current.top_selling_combos?.length ?? 0) > 0;

  return (
    <DashLayout>
      <RoleProtectedPage
        allowedRoles={[Roles.manager, Roles.owner]}
        allowedPermissions={["dashboard.view"]}
      >
        <div className="space-y-8">
          <PageTitle title="Tableau de bord" description="Suivez les performances de votre restaurant en temps réel" />

          {/* Date Range Picker */}
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onDateChange={handleDateChange}
            compareMode={compareMode}
            onCompareModeChange={setCompareMode}
          />

          {/* KPI Error */}
          {kpiError && (
            <ErrorMessage
              message="Impossible de charger les indicateurs de performance"
              onRetry={refetchKpi}
            />
          )}

          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <KPICard
              title="Total Commandes"
              value={kpiData?.data.current.total_orders ?? 0}
              previousValue={kpiData?.data.previous?.total_orders}
              format="number"
              icon={<ShoppingCart />}
              loading={kpiLoading}
              description="Nombre total de commandes passées durant la période sélectionnée"
            />
            <KPICard
              title="Total facturé"
              value={kpiData?.data.current.total_revenue ?? 0}
              previousValue={kpiData?.data.previous?.total_revenue}
              format="currency"
              icon={<Receipt />}
              loading={kpiLoading}
              description="Valeur facturée des commandes valides de la période (articles + frais de livraison), encaissée ou non"
            />
            <KPICard
              title="Panier moyen"
              value={kpiData?.data.current.average_order_value ?? 0}
              previousValue={kpiData?.data.previous?.average_order_value}
              format="currency"
              icon={<TrendingUp />}
              loading={kpiLoading}
              description="Valeur moyenne d'une commande (total facturé ÷ nombre de commandes valides)"
            />
            <KPICard
              title="Taux d'acceptation"
              value={kpiData?.data.current.order_acceptance_rate ?? 0}
              previousValue={kpiData?.data.previous?.order_acceptance_rate}
              format="percentage"
              icon={<CheckCircle2 />}
              loading={kpiLoading}
              description="Pourcentage de commandes acceptées (non annulées ou refusées)"
            />
          </div>

          {/* Customer Engagement Error */}
          {engagementError && (
            <ErrorMessage
              message="Impossible de charger les données d'engagement client"
              onRetry={refetchEngagement}
            />
          )}

          {/* Customer Engagement KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <KPICard
              title="Vues de page"
              value={engagementData?.data.current.total_page_views ?? 0}
              previousValue={engagementData?.data.previous?.total_page_views}
              format="number"
              icon={<Eye />}
              loading={engagementLoading}
              description="Nombre de fois que des clients ont consulté votre menu après avoir scanné le QR code"
            />
            <KPICard
              title="Visiteurs uniques"
              value={engagementData?.data.current.total_unique_visitors ?? 0}
              previousValue={
                engagementData?.data.previous?.total_unique_visitors
              }
              format="number"
              icon={<Users />}
              loading={engagementLoading}
              description="Nombre de clients différents qui ont scanné votre QR code et consulté le menu"
            />
            <KPICard
              title="Taux de conversion"
              value={engagementData?.data.current.conversion_rate ?? 0}
              previousValue={engagementData?.data.previous?.conversion_rate}
              format="percentage"
              icon={<MousePointer />}
              loading={engagementLoading}
              description="Pourcentage de clients qui ont passé commande après avoir consulté le menu"
            />
            <KPICard
              title="Commandes/jour"
              value={kpiData?.data.current.average_orders_per_day ?? 0}
              previousValue={kpiData?.data.previous?.average_orders_per_day}
              format="number"
              icon={<Package />}
              loading={kpiLoading}
              description="Nombre moyen de commandes reçues par jour sur la période"
            />
          </div>

          {/* Time Analytics Section */}
          <section className="space-y-6">
            <h2 className="text-xl font-semibold text-card-foreground">
              Analyse temporelle
            </h2>

            {timeError && (
              <ErrorMessage
                message="Impossible de charger les données d'analyse temporelle"
                onRetry={refetchTime}
              />
            )}

            <DailyTrendsChart
              data={timeData?.data.current.daily_trends || []}
              previousData={timeData?.data.previous?.daily_trends}
              loading={timeLoading}
            />

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <HourlyDistributionChart
                data={timeData?.data.current.hourly_distribution || []}
                previousData={timeData?.data.previous?.hourly_distribution}
                loading={timeLoading}
              />
              <DayOfWeekChart
                data={timeData?.data.current.day_of_week || []}
                previousData={timeData?.data.previous?.day_of_week}
                loading={timeLoading}
              />
            </div>
          </section>

          {/* Menu Performance Section */}
          <section className="space-y-6">
            <h2 className="text-xl font-semibold text-card-foreground">
              Performance du menu
            </h2>

            {menuError && (
              <ErrorMessage
                message="Impossible de charger les données de performance du menu"
                onRetry={refetchMenu}
              />
            )}

            {/* Single grid so the cards reflow without gaps when the combos
                card is hidden for restaurants that don't use combos. */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <TopSellingItems
                items={menuData?.data.current.top_selling_items || []}
                previousItems={menuData?.data.previous?.top_selling_items}
                loading={menuLoading}
                type="top"
              />
              {showCombos && (
                <TopSellingItems
                  items={menuData?.data.current.top_selling_combos || []}
                  previousItems={menuData?.data.previous?.top_selling_combos}
                  loading={menuLoading}
                  type="combo"
                />
              )}
              <TopSellingItems
                items={menuData?.data.current.least_popular_items || []}
                previousItems={menuData?.data.previous?.least_popular_items}
                loading={menuLoading}
                type="least"
              />
              <SectionBreakdownChart
                data={menuData?.data.current.menu_section_breakdown || []}
                previousData={menuData?.data.previous?.menu_section_breakdown}
                loading={menuLoading}
              />
            </div>
          </section>

          {/* Customer Engagement Section */}
          <section className="space-y-6">
            <h2 className="text-xl font-semibold text-card-foreground">
              Engagement client
            </h2>

            <EngagementTrendsChart
              data={engagementData?.data.current.daily_trends || []}
              previousData={engagementData?.data.previous?.daily_trends}
              loading={engagementLoading}
            />
          </section>
        </div>
      </RoleProtectedPage>
    </DashLayout>
  );
}

export default Dashboard;
